import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { Pipeline, PipelinePermission } from "@/types/pipeline";


export function usePipelinePermissions() {
  const user = useSelector((state: RootState) => state.auth.user);
  
  const isAdmin = useMemo(() => {
    if (!user?.role?.name) return false;
    return user.role.name === "Super Admin" || user.role.name === "Admin";
  }, [user?.role?.name]);

  const roleId = user?.role?.id;
  
  const filterPipelinesByPermissions = (pipelines: Pipeline[]): Pipeline[] => {
    if (isAdmin) {
      console.log('Usuario es admin');
      return pipelines;
    }

    if (!roleId) {
      console.log(' No roleId encontrado');
      return [];
    }

    const filtered = pipelines.filter((pipeline) => {
      // Si el pipeline no tiene permisos (principal)
      if (Array.isArray(pipeline.permissions) && pipeline.permissions.length === 0) {
        console.log('Pipeline es principal (no permissions)');
        return false;
      }

      // Verificar si el usuario tiene permisos en este pipeline
      const hasPermission = pipeline.permissions?.some(
        (perm) => {
          const matches = perm.role_id === roleId && perm.can_view;
          return matches;
        }
      );
      return hasPermission;
    });
    return filtered;
  };

  const canViewPipeline = (pipeline: Pipeline): boolean => {
    if (isAdmin) return true;
    if (!roleId) return false;

    // Pipeline principal solo para admins
    if (Array.isArray(pipeline.permissions) && pipeline.permissions.length === 0) {
      return false;
    }

    return pipeline.permissions?.some(
      (perm) => perm.role_id === roleId && perm.can_view
    ) || false;
  };


  const canEditPipeline = (pipeline: Pipeline): boolean => {
    if (isAdmin) return true;
    if (!roleId) return false;

    if (Array.isArray(pipeline.permissions) && pipeline.permissions.length === 0) {
      return false;
    }

    return pipeline.permissions?.some(
      (perm) => perm.role_id === roleId && perm.can_edit
    ) || false;
  };


  const canDeletePipeline = (pipeline: Pipeline): boolean => {
    if (isAdmin) return true;
    if (!roleId) return false;

    if (Array.isArray(pipeline.permissions) && pipeline.permissions.length === 0) {
      return false;
    }

    return pipeline.permissions?.some(
      (perm) => perm.role_id === roleId && perm.can_delete
    ) || false;
  };


  const getAccessiblePipelineIds = (pipelines: Pipeline[]): string[] => {
    const accessiblePipelines = filterPipelinesByPermissions(pipelines);
    return accessiblePipelines.map((p) => p.id);
  };


  const canCreateOpportunityInPipeline = (pipeline: Pipeline): boolean => {
    if (isAdmin) return true;
    if (!roleId) return false;
    if (Array.isArray(pipeline.permissions) && pipeline.permissions.length === 0) {
      return false;
    }

    return pipeline.permissions?.some(
      (perm) => perm.role_id === roleId && (perm.can_edit || perm.can_view)
    ) || false;
  };

  return {
    isAdmin,
    roleId,
    filterPipelinesByPermissions,
    canViewPipeline,
    canEditPipeline,
    canDeletePipeline,
    getAccessiblePipelineIds,
    canCreateOpportunityInPipeline,
  };
}
